import type {
  AIModelChoices,
  BuyerSampleDetails,
  Evidence,
  EvidenceUploadMode,
  PaginatedEvidenceList,
  SampleDetails,
  SampleVariant,
} from 'api';
import { formatDate } from 'date';
import { useTranslation } from 'i18n';
import { useState } from 'react';
import { MediaDialog, type PhotoEvidenceItem, PhotoEvidenceList, Stack, Typography, TypographyIcon } from 'ui';
import { CalendarMonth, Notes, Person, Scale } from 'ui/icons';

import { AiFeedbackVisibilityProvider, AiFilters, AnnotatedMediaItem, useAnnotations } from '../../../annotations';
import { AiSampleBreakdown, MaterialSampleBreakdown } from '../../../breakdowns';
import { useOfflineEvidence } from '../../hooks/useOfflineEvidence/useOfflineEvidence';
import { OfflineEvidenceGallery } from '../OfflineEvidenceGallery/OfflineEvidenceGallery';

export const SampleBreakdown = ({
  isMobile,
  displayManualBreakdown = true,
  sample,
  model,
  evidence,
  variant,
  mode,
  onSuccessfulEvidenceSync,
}: {
  isMobile: boolean;
  sample: BuyerSampleDetails | SampleDetails;
  model?: AIModelChoices;
  displayManualBreakdown?: boolean;
  evidence: PaginatedEvidenceList;
  variant: SampleVariant;
  mode: EvidenceUploadMode;
  onSuccessfulEvidenceSync?: () => void;
}) => {
  const { t } = useTranslation('features');
  const [selectedEvidence, setSelectedEvidence] = useState<Evidence | null>(null);

  const parentId = sample.id;
  // TODO: https://linear.app/safi/issue/AI-1884/refactor-samplebreakdown-to-use-useevidenceanalysis
  const { offlinePhotos } = useOfflineEvidence({
    mode,
    parentId: parentId,
  });

  const onlineCarouselMedia = evidence.results.map((file) => String(file.file));

  const {
    selectedAnnotation,
    handleImageChange,
    hiddenCategories,
    handleHideCategory,
    handleToggleLegend,
    showLegend,
  } = useAnnotations({ model });

  const handleSelectOnline = (src: string) => {
    const relatedEvidence = evidence.results.find((item) => item.file === src || item.preview === src);
    setSelectedEvidence(relatedEvidence || null);
    handleImageChange([...(relatedEvidence?.model_results?.coco_prediction || [])]);
  };

  return (
    <>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        data-testid="sample-breakdown"
        sx={{
          gap: 3,
        }}
      >
        <Stack
          sx={{
            gap: 2,
            width: isMobile ? '100%' : '50%',
          }}
        >
          <Stack
            sx={{
              gap: 1,
            }}
          >
            <Typography variant="body1" fontWeight="bold">
              {t('evidence.sampleBreakdown.labels.sample')}
            </Typography>
            <Stack
              direction="row"
              useFlexGap
              sx={{
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <TypographyIcon size="small" variant="body2" icon={CalendarMonth}>
                {formatDate(sample.date)}
              </TypographyIcon>
              {sample.user?.full_name ? (
                <TypographyIcon size="small" variant="body2" icon={Person}>
                  {sample.user.full_name}
                </TypographyIcon>
              ) : null}
              <TypographyIcon size="small" variant="body2" icon={Scale}>
                {t('evidence.sampleBreakdown.totalWeight', { values: { totalWeight: sample.total_weight } })}
              </TypographyIcon>
              <TypographyIcon size="small" variant="body2" icon={Notes}>
                {sample.description || t('evidence.sampleBreakdown.labels.noNotes')}
              </TypographyIcon>
            </Stack>
          </Stack>
          <Stack
            sx={{
              gap: 5,
            }}
          >
            {displayManualBreakdown ? (
              <Stack
                sx={{
                  gap: 1,
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  {t('evidence.sampleBreakdown.labels.manualBreakdown')}
                </Typography>
                <MaterialSampleBreakdown
                  variant={variant}
                  items={sample.breakdown.map((item) => ({
                    label: item.name_display,
                    unit: item.unit,
                    unit_display: item.unit_display,
                    value: item.value,
                  }))}
                />
              </Stack>
            ) : null}

            {sample.alu_loose_ai_composition || sample.pet_loose_ai_composition ? (
              <Stack
                sx={{
                  gap: 1,
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  {t('evidence.sampleBreakdown.labels.aiBreakdown')}
                </Typography>
                <AiSampleBreakdown
                  model={sample.alu_loose_ai_composition ? 'alu_loose' : 'pet_loose'}
                  totalWeight={sample.total_weight || undefined}
                  composition={sample.alu_loose_ai_composition || sample.pet_loose_ai_composition || undefined}
                />
              </Stack>
            ) : null}
          </Stack>
        </Stack>

        <Stack
          sx={{
            gap: 1,
            width: isMobile ? '100%' : '50%',
          }}
        >
          <Typography variant="body1" fontWeight="bold">
            {t('evidence.sampleBreakdown.labels.sampleEvidence')}
          </Typography>
          <PhotoEvidenceList
            openOnClick
            slotProps={{
              imageList: {
                cols: isMobile ? 3 : 5,
              },
            }}
            evidencePhotos={evidence.results.map(({ file, preview }) => ({
              src: (preview || file) ?? '',
            }))}
            handleClick={(photo: PhotoEvidenceItem) => handleSelectOnline(photo.src)}
          />

          {offlinePhotos.length > 0 ? (
            <OfflineEvidenceGallery parentId={String(parentId)} mode={mode} onSyncComplete={onSuccessfulEvidenceSync} />
          ) : null}
        </Stack>
      </Stack>
      <AiFeedbackVisibilityProvider>
        <MediaDialog
          open={Boolean(selectedEvidence?.file)}
          selectedMedium={selectedEvidence?.file ?? ''}
          onClose={() => setSelectedEvidence(null)}
          media={onlineCarouselMedia}
          handleNavigate={handleSelectOnline}
          imageCount={onlineCarouselMedia.length}
          slotProps={{
            imageCarousel: {
              slotProps: {
                ...(selectedAnnotation && selectedEvidence && model
                  ? {
                      mediaItemSlot: (
                        <AnnotatedMediaItem
                          image={selectedEvidence.file ?? ''}
                          annotations={selectedAnnotation}
                          hiddenCategories={hiddenCategories}
                          showLegend={showLegend}
                          model={model}
                          showAerosols={true}
                          evidenceId={selectedEvidence.id}
                        />
                      ),
                      sidebar: {
                        children: (
                          <AiFilters
                            selectedAnnotation={selectedAnnotation}
                            hiddenCategories={hiddenCategories}
                            handleHideCategory={handleHideCategory}
                            handleToggleLegend={handleToggleLegend}
                            showLegend={showLegend}
                            model={model}
                            showAerosols
                          />
                        ),
                      },
                    }
                  : null),
              },
            },
          }}
        />
      </AiFeedbackVisibilityProvider>
    </>
  );
};
